import axios from 'axios';
import https from 'https';
import { getCpapApiEndpoint, setAxiosDefaultHeaders } from './apiBaseUtilities';
import { CpapUserSchemaProps, emptyUserMongoSchema, emptySubjectState, AssessmentSchemaProps } from './mongoSchemas';

import {
    UserProps,
    FirebaseUserProps,
    SubjectProps
} from './entityUtilities';
import { useUserState } from '../context/UserContext';
import { debugLog } from './debugUtilities';

const cpapApiEndpoint = getCpapApiEndpoint();

// A distinct error type so callers (currently the login flow) can tell
// "the backend was unreachable / errored" apart from "the backend responded
// but had no record." The helpers below already swallow most errors and
// return undefined; for login we need to surface the network-error case so
// we don't treat an outage as "brand-new user."
export class ApiUnreachableError extends Error {
  constructor(message = 'API unreachable') {
    super(message);
    this.name = 'ApiUnreachableError';
  }
}

// Decide whether an axios error indicates the backend was unreachable
// (network failure, DNS, CORS preflight, dropped TCP, no response) vs.
// a real HTTP response we just don't like (404/401/422 etc.). The signal
// is whether the error carries an HTTP response object.
function isNetworkLevelAxiosError(err: any): boolean {
  if (!err) return false;
  if (err.response) return false; // server responded with some status
  // axios sets code='ERR_NETWORK' on network/CORS errors in v1.x;
  // older versions just leave response undefined.
  return true;
}


export async function getCpapUserWithFilter(firebaseUser:FirebaseUserProps|undefined, email:any)
:Promise<CpapUserSchemaProps|undefined>{
  if(!cpapApiEndpoint || !firebaseUser || email == undefined){
    return emptyUserMongoSchema;
  }

  const {uid, bearerToken } = firebaseUser;
  const apiendpoint = `${cpapApiEndpoint}users/email/${email}`;
  setAxiosDefaultHeaders(uid, bearerToken);
  try{
  debugLog('api endpt: ', apiendpoint);
//   debugLog('uid:', uid);
//   debugLog('token:', bearerToken);
    const responseData = await axios.get(apiendpoint);
    //debugLog('response:', responseData);
    if(!responseData.data) return undefined;
    if(responseData.data.length === 0) return undefined;
    return responseData.data[0];
  }catch(error: any){
    // Distinguish "API unreachable" from "API said no". The login flow needs
    // to know the difference so it doesn't treat an outage as a brand-new user.
    if (isNetworkLevelAxiosError(error)){
      debugLog('NETWORK ERROR on /users/email:', error);
      throw new ApiUnreachableError('GET /users/email failed (network)');
    }
    debugLog('HTTP error on /users:', error);
    return undefined;
  }
}

export async function getCribsyUserByUid(firebaseUser:FirebaseUserProps|undefined, uid:string)
:Promise<CpapUserSchemaProps|undefined>{
  if(!cpapApiEndpoint || !firebaseUser){
    return emptyUserMongoSchema;
  }

  const {uid:userUid, bearerToken } = firebaseUser;
  const apiendpoint = `${cpapApiEndpoint}users/uid/${uid}`;
  setAxiosDefaultHeaders(userUid, bearerToken);
  try{
  debugLog('api endpt: ', apiendpoint);
//   debugLog('uid:', uid);
//   debugLog('token:', bearerToken);
    const responseData = await axios.get(apiendpoint);
    //debugLog('response:', responseData);
    if(!responseData.data) return undefined;
    if(responseData.data.length === 0) return undefined;
    return responseData.data[0];
  }catch(error){
    debugLog('401 ERROR on /users');
    return undefined;
  }
}


export async function getCpapUserById(firebaseUser:FirebaseUserProps|undefined, id:string)
:Promise<CpapUserSchemaProps|undefined>{
  if(!cpapApiEndpoint || !firebaseUser){
    return emptyUserMongoSchema;
  }

  const {uid, bearerToken } = firebaseUser;
  const apiendpoint = `${cpapApiEndpoint}users/uid/${id}`;
  setAxiosDefaultHeaders(uid, bearerToken);
  try{
  debugLog('api endpt: ', apiendpoint);
//   debugLog('uid:', uid);
//   debugLog('token:', bearerToken);
    const responseData = await axios.get(apiendpoint);
    //debugLog('response:', responseData);
    if(!responseData.data) return undefined;
    if(responseData.data.length === 0) return undefined;
    return responseData.data[0];
  }catch(error: any){
    if (isNetworkLevelAxiosError(error)){
      debugLog('NETWORK ERROR on /users/uid:', error);
      throw new ApiUnreachableError('GET /users/uid failed (network)');
    }
    debugLog('HTTP error on /users/uid:', error);
    return undefined;
  }
}


export async function getSubjectDetails(firebaseUser:FirebaseUserProps,parentid: string|undefined)
  :Promise<SubjectProps|undefined>{
  if (!parentid) return undefined;
  if (!cpapApiEndpoint || parentid == null || parentid == ''){
    return undefined;
  }

  debugLog('Getting subject with parent:', parentid);
  const {uid, bearerToken } = firebaseUser;
  const apiendpoint = `${cpapApiEndpoint}subjects/parent/${parentid}`;
  setAxiosDefaultHeaders(uid, bearerToken);
  try{
//   debugLog('uid:', uid);
//   debugLog('token:', bearerToken);
    const responseData = await axios.get(apiendpoint);
    debugLog('subjects get api response:', responseData);
    if(!responseData.data) return undefined;
    if(responseData.data.length === 0) return undefined;
    return responseData.data[0];
  }catch(error){
    debugLog('401 ERROR on /subjects/parent/:id');
    return undefined;
  }}

export async function getSubjectFromId(firebaseUser:FirebaseUserProps,id: string|undefined)
:Promise<SubjectProps|undefined>{
  if (!id) return undefined;
  if (!cpapApiEndpoint || id == null || id == ''){
    return undefined;
  }

  debugLog('Getting subject with id:', id);
  const {uid, bearerToken } = firebaseUser;
  const apiendpoint = `${cpapApiEndpoint}subjects/${id}`;
  setAxiosDefaultHeaders(uid, bearerToken);
  try{
  //   debugLog('uid:', uid);
  //   debugLog('token:', bearerToken);
    const responseData = await axios.get(apiendpoint);
    //debugLog('subjects get api response:', responseData.data);
    if(!responseData.data) return undefined;
    return responseData.data;
  }catch(error){
    debugLog('401 ERROR on /subjects/:id');
    return undefined;
  }
}

export async function getLatestAssesment(firebaseUser:FirebaseUserProps, subjectId: string|undefined):
  Promise<AssessmentSchemaProps|undefined>{

    if(!cpapApiEndpoint){
      return undefined;
    }

    const {uid, bearerToken } = firebaseUser;
    const apiendpoint = `${cpapApiEndpoint}assessments/latest/${subjectId}`;
    setAxiosDefaultHeaders(uid, bearerToken);
    try{

      const responseData = await axios.get(apiendpoint);
      //debugLog('Asmts get api response:', responseData);
      if(!responseData.data) return undefined;
      return responseData.data;

    } catch (error){
      debugLog('ERROR on /assessments:', error);
    }

  }


export async function getAssesmentById(firebaseUser:FirebaseUserProps, id: string|undefined):
Promise<AssessmentSchemaProps|undefined>{
  if(!cpapApiEndpoint){
    return undefined;
  }

  const {uid, bearerToken } = firebaseUser;
  const apiendpoint = `${cpapApiEndpoint}assessments/${id}`;
  setAxiosDefaultHeaders(uid, bearerToken);
  try{

    const responseData = await axios.get(apiendpoint);
    //debugLog('Asmts get api response:', responseData);
    if(!responseData.data) return undefined;
    return responseData.data;

  } catch (error){
    debugLog('ERROR on /assessments:', error);
  }
  

}

  export async function getAllAssesments(firebaseUser:FirebaseUserProps):
  Promise<AssessmentSchemaProps|undefined>{

    if(!cpapApiEndpoint || !firebaseUser || firebaseUser?.uid == ''){
      return undefined;
    }

    //debugLog('firebaseUser:', firebaseUser);
    const {uid, bearerToken } = firebaseUser;
    if(bearerToken){
      debugLog('[getAllAssessments] valid token passed');
    }
    const apiendpoint = `${cpapApiEndpoint}assessments`;
    setAxiosDefaultHeaders(uid, bearerToken);
    try{

      debugLog('Hitting endpoint:', apiendpoint);
      const responseData = await axios.get(apiendpoint);
      //debugLog('Asmts get api response:', responseData);
      if(!responseData.data) return undefined;
      return responseData.data;

    } catch (error){
      debugLog('ERROR on /assessments:', error);
    }

  }

  export async function getAssessmentDetails(firebaseUser:FirebaseUserProps, assessmentId:string):
  Promise<any>{

    if(!cpapApiEndpoint || !firebaseUser || firebaseUser?.uid == '' || !assessmentId){
      return undefined;
    }

    const {uid, bearerToken } = firebaseUser;
    const apiendpoint = `${cpapApiEndpoint}assessments/${assessmentId}/details`;
    setAxiosDefaultHeaders(uid, bearerToken);
    try{

      debugLog('Hitting endpoint:', apiendpoint);
      const responseData = await axios.get(apiendpoint);
      if(!responseData.data) return undefined;
      return responseData.data;

    } catch (error){
      debugLog('ERROR on /assessments/:id/details:', error);
      throw error;
    }

  }




  export async function getSubjectByEmail(firebaseUser:FirebaseUserProps, email:string):
  Promise<SubjectProps|undefined>{

    if(!cpapApiEndpoint || !firebaseUser || firebaseUser?.uid == ''){
      return undefined;
    }

    debugLog('firebaseUser:', firebaseUser);
    const {uid, bearerToken } = firebaseUser;
    if(bearerToken){
      debugLog('[getAllAssessments] valid token passed');
    }
    const apiendpoint = `${cpapApiEndpoint}users/subjectbyemail/${email}`;
    setAxiosDefaultHeaders(uid, bearerToken);
    try{

      debugLog('Hitting endpoint:', apiendpoint);
      const responseData = await axios.get(apiendpoint);
      debugLog('Subject get api response:', responseData);
      if(!responseData.data) return undefined;
      return responseData.data;

    } catch (error){
      debugLog('ERROR on /users/subjectbyemail:', error);
    }

  }

  export async function getFilteredAssesments(firebaseUser:FirebaseUserProps, email:string, filter:any):
  Promise<AssessmentSchemaProps[]|undefined>{

    if(!cpapApiEndpoint || !firebaseUser || firebaseUser?.uid == ''){
      return undefined;
    }

    debugLog('Filter:', filter);
    const {uid, bearerToken } = firebaseUser;
    if(bearerToken){
      debugLog('[getFilteredAssessments] valid token passed');
    }

    const apiendpoint = `${cpapApiEndpoint}assessments/filter/${filter}`;
    setAxiosDefaultHeaders(uid, bearerToken);
    try{

      debugLog('Hitting endpoint:', apiendpoint);
      const responseData = await axios.get(apiendpoint);
      //debugLog('Asmts get api response:', responseData);
      if(!responseData.data) return undefined;
      return responseData.data;

    } catch (error){
      debugLog('ERROR on /assessments:', error);
    }

  }

  export async function getSampleAssesments(firebaseUser:FirebaseUserProps):
  Promise<AssessmentSchemaProps|undefined>{

    if(!cpapApiEndpoint || !firebaseUser || firebaseUser?.uid == ''){
      return undefined;
    }

    //debugLog('firebaseUser:', firebaseUser);
    const {uid, bearerToken } = firebaseUser;
    if(bearerToken){
      debugLog('[getSampleAssessments] valid token passed');
    }
    const apiendpoint = `${cpapApiEndpoint}assessments/samples`;
    setAxiosDefaultHeaders(uid, bearerToken);
    try{

      debugLog('Hitting endpoint:', apiendpoint);
      const responseData = await axios.get(apiendpoint);
      //debugLog('Asmts get api response:', responseData);
      if(!responseData.data) return undefined;
      return responseData.data;

    } catch (error){
      debugLog('ERROR on /sampleassessments:', error);
    }

  }



  export async function getNotifications(firebaseUser:FirebaseUserProps|undefined){
    let notifications:any[] = [];
    if(!cpapApiEndpoint){
      notifications = [];
    }else{
      if (!firebaseUser){ return;
      }
      const {uid, bearerToken } = firebaseUser;
      const url = `${cpapApiEndpoint}notifications`;
      setAxiosDefaultHeaders(uid, bearerToken);
      try{
        const responseData = await axios.get(url);
        debugLog('Notifications:', responseData.data);
        notifications = responseData.data;
      }catch(error){
        debugLog('401 ERROR on /notifications');          
      }
    }
    return notifications;
  }

export async function checkIfEmailIsTaken(firebaseUser:FirebaseUserProps|undefined, email:String) : Promise<boolean>{

    if(!cpapApiEndpoint || !firebaseUser){
      return false;
    }
  
    debugLog('Checking if email is taken:', email);
  
    let emailLowerCase = email.toLowerCase();
    const {uid, bearerToken } = firebaseUser;
    const filter = {'where':{'email':emailLowerCase } };
  
    const apiendpoint = `${cpapApiEndpoint}users/email/${emailLowerCase}`;
    setAxiosDefaultHeaders(uid, bearerToken);
    // try{
    const responseData = await axios.get(apiendpoint);
  
    debugLog('Is email taken:', responseData);
    if(!responseData.data) return false;
    if(responseData.data.length === 0) return false;
  
    //this email is taken, meaning there's some emmauser entry that has this email
    //
    //what if the user has retyped their own email back?
    if (firebaseUser.uid == responseData.data[0].uid){
      debugLog('firebaseuser uid:', firebaseUser.uid);
      debugLog('found cpapuser:', responseData.data[0]);
      return false;
    }
  
    return true;
  
  }

// Used to set up the user that is logged in, so don't the subject is needed.
export async function getUserDataWithEmail(email:string, bearerToken:string, uid:string){
    if(!cpapApiEndpoint){
      return null;
    }
    const firebaseUser:FirebaseUserProps = {
      uid:uid,
      bearerToken:bearerToken,
      email:email,
      photoURL:'',
      displayName:email,
      firebaseUserObject:null
    };
    return getCpapUserWithFilter(firebaseUser, email);
  }
  


  // when users register with PHONE auth, firebaseUser object might not have email
  // but the user might still be in the database under emmauser collection
  export async function getUserDataWithUid (uid: string, bearerToken: string) {
  
    
    if(!cpapApiEndpoint){
      return null;
    }
    
    const firebaseUser:FirebaseUserProps = {
      uid:uid,
      bearerToken:bearerToken,
      email:'',
      photoURL:'',
      displayName:'',
      firebaseUserObject:null
    };
    return getCpapUserById(firebaseUser, uid);
  }


  export async function getParentFromEmail(firebaseUser: FirebaseUserProps, email:string){


    if(!cpapApiEndpoint){
      return null;
    }
    
    const {uid, bearerToken } = firebaseUser;
    
    const apiendpoint = `${cpapApiEndpoint}users/email/${email}`;
    setAxiosDefaultHeaders(uid, bearerToken);
    try{

      debugLog('Hitting endpoint:', apiendpoint);
      const responseData = await axios.get(apiendpoint);
      debugLog('Users get api response:', responseData);
      if(!responseData.data) return undefined;
      return responseData.data;

    } catch (error){
      debugLog('ERROR on /users/:id', error);
    }
  }

  export async function getSubjectFromParentEmail (firebaseUser:FirebaseUserProps,email: string) {
  
    if(!cpapApiEndpoint){
      return null;
    }
    
    const {uid, bearerToken } = firebaseUser;
    
    const parent = await getParentFromEmail(firebaseUser, email);
    debugLog('parent:', parent);
    if(!parent || parent.length == 0) return undefined;
    const parentid = parent[0]._id;

    const apiendpoint = `${cpapApiEndpoint}subjects/parent/${parentid}`;
    setAxiosDefaultHeaders(uid, bearerToken);
    try{

      debugLog('Hitting endpoint:', apiendpoint);
      const responseData = await axios.get(apiendpoint);
      debugLog('Subject get api response:', responseData.data);
      if(!responseData.data) return undefined;
      return responseData.data;

    } catch (error){
      debugLog('ERROR on /subjects/:id', error);
    }

  }


export async function getAllCpapUsers(user:UserProps):Promise<any[]>{
    if(!cpapApiEndpoint){
      // might be good to have some data for this
      return [];
    }
  
    let rawUserData:any = [];
    if(!user.firebaseUser) return rawUserData;
    const {uid, bearerToken } = user.firebaseUser;
    setAxiosDefaultHeaders(uid, bearerToken);

  
    const userUrl = `${cpapApiEndpoint}users`;
  
    const usersResponse = await axios.get(userUrl);
    rawUserData = usersResponse.data;

    return rawUserData;
  }


  // check for presence of studyid in the StudyParticipant collection
  // return '' string if no studyid entry for this parent email
  export async function checkForStudyId(firebaseUser: FirebaseUserProps|undefined, email:String):
  Promise<string> {

    const studyid = '';
    if(!cpapApiEndpoint || !firebaseUser){
      return studyid;
    }
    if(email === undefined) { return studyid};
  
    let emailLowerCase = email.toLowerCase();
    const {uid, bearerToken } = firebaseUser;

    const apiendpoint = `${cpapApiEndpoint}study-participants/email/${emailLowerCase}`;
    setAxiosDefaultHeaders(uid, bearerToken);
  
    const responseData = await axios.get(apiendpoint);
  
    if(!responseData.data) return studyid;
    if(responseData.data.length === 0) return studyid;
  
    let participantData =  responseData.data[0];
  
    if (participantData?.studyid?.toLowerCase() !== ''){
      return participantData.studyid;
    }

    return studyid;

  }

  export async function checkIfStudyidValid(firebaseUser:FirebaseUserProps|undefined, email:String, studyid: String) : 
  Promise<boolean> {
  
    if(!cpapApiEndpoint || !firebaseUser){
      return false;
    }
    if(email === undefined) { return false};
  
    let emailLowerCase = email.toLowerCase();
    const {uid, bearerToken } = firebaseUser;
  
    if (studyid === undefined) {return false};
  
    let studyidLowerCase = studyid.toLowerCase();
  
    //const filter = { 'where': { 'and': [{'email': emailLowerCase } , {'studyid': studyidLowerCase }] } };
  
    const filter = {'where':{'email':emailLowerCase } };
    const apiendpoint = `${cpapApiEndpoint}study-participants/email/${emailLowerCase}`;
    setAxiosDefaultHeaders(uid, bearerToken);
  
    const responseData = await axios.get(apiendpoint);
  
    if(!responseData.data) return false;
    if(responseData.data.length === 0) return false;
  
    let participantData =  responseData.data[0];
  
    if (participantData?.studyid?.toLowerCase() === studyidLowerCase){
      return true;
    }
  
  
    return false;
  }
  

